import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { getApiAuthHeaders, getApiUrl } from '../utils/api';
import { isKnownLiveResource, parseSse, queryTouchedBy } from './liveUpdateRules';

/** Mehrere Änderungen kurz hintereinander lösen ein gemeinsames Neuladen aus. */
const FLUSH_DELAY_MS = 500;
const RETRY_MIN_MS = 2_000;
const RETRY_MAX_MS = 30_000;
/** Der Server sendet alle 25 s ein "ping" — bleibt es so lange still, ist die Verbindung tot. */
const SILENCE_LIMIT_MS = 70_000;
/** War der Reiter länger verborgen, wird beim Zurückkehren alles Sichtbare neu geladen. */
const CATCH_UP_AFTER_MS = 20_000;

/**
 * Hält offene Seiten aktuell: ändert jemand in der Agentur etwas (auch in einem
 * anderen Reiter oder an einem anderen Arbeitsplatz), werden die betroffenen
 * Abfragen neu geladen — ohne die Seite neu zu laden.
 *
 * Die Verbindung besteht nur, solange der Reiter sichtbar ist; beim
 * Zurückkehren und nach einem Abbruch wird nachgeladen, was verpasst wurde.
 * Fällt der Kanal ganz aus, arbeitet die Anwendung wie bisher weiter.
 */
const LiveUpdates: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const agencyId = isAuthenticated ? user?.agencyId : undefined;

  useEffect(() => {
    if (!agencyId || typeof fetch !== 'function' || typeof ReadableStream === 'undefined') return undefined;

    let stopped = false;
    let controller: AbortController | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    let silenceTimer: ReturnType<typeof setTimeout> | undefined;
    let retryDelay = RETRY_MIN_MS;
    let missedSomething = false;
    let hiddenSince = 0;
    const pending = new Set<string>();

    const flush = () => {
      flushTimer = undefined;
      const resources = Array.from(pending);
      pending.clear();
      if (resources.length === 0) return;
      void queryClient.invalidateQueries({ predicate: (query) => queryTouchedBy(resources, query.queryKey) });
    };

    const onChange = (resource: string) => {
      if (!isKnownLiveResource(resource)) return;
      pending.add(resource);
      if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
    };

    const armSilenceTimer = () => {
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => controller?.abort(), SILENCE_LIMIT_MS);
    };

    const scheduleRetry = () => {
      if (stopped || document.hidden) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
    };

    async function connect() {
      if (stopped || document.hidden || controller) return;
      const current = new AbortController();
      controller = current;
      let established = false;
      try {
        const headers = await getApiAuthHeaders();
        if (!headers) throw new Error('not signed in');
        const response = await fetch(`${getApiUrl()}/live-updates/stream`, {
          headers: { ...headers, Accept: 'text/event-stream' },
          signal: current.signal,
          cache: 'no-store',
        });
        if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        armSilenceTimer();
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          armSilenceTimer();
          const parsed = parseSse(buffer + decoder.decode(value, { stream: true }));
          buffer = parsed.rest;
          for (const message of parsed.messages) {
            if (message.event === 'ready') {
              // Erst jetzt steht die Verbindung wirklich: ohne gültige Anmeldung
              // antwortet der Server zwar mit 200, sendet aber nur "error" und
              // schließt — das darf weder die Wartezeit zurücksetzen noch nachladen.
              established = true;
              retryDelay = RETRY_MIN_MS;
              if (missedSomething) {
                // Während der Unterbrechung kann alles Mögliche geändert worden sein.
                missedSomething = false;
                void queryClient.invalidateQueries();
              }
              continue;
            }
            if (message.event !== 'change') continue;
            try {
              onChange(String(JSON.parse(message.data)?.resource || ''));
            } catch {
              /* unlesbare Nachricht überspringen */
            }
          }
        }
      } catch {
        /* Abbruch oder Netzfehler — unten wird neu verbunden */
      } finally {
        clearTimeout(silenceTimer);
        if (controller === current) controller = null;
        if (!stopped && !document.hidden) {
          if (established) missedSomething = true;
          scheduleRetry();
        }
      }
    }

    const onVisibility = () => {
      if (document.hidden) {
        hiddenSince = Date.now();
        clearTimeout(retryTimer);
        controller?.abort();
        return;
      }
      if (hiddenSince && Date.now() - hiddenSince > CATCH_UP_AFTER_MS) missedSomething = true;
      hiddenSince = 0;
      retryDelay = RETRY_MIN_MS;
      void connect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    void connect();

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(retryTimer);
      clearTimeout(flushTimer);
      clearTimeout(silenceTimer);
      controller?.abort();
    };
  }, [agencyId, queryClient]);

  return null;
};

export default LiveUpdates;
